/**
 * Usafe — Quantity Bundle Upsell with per-journal colour selection
 *
 * Buy 1  → sets quantity on Dawn's native form + clicks submit
 *           (cart drawer / notification handled by Dawn as normal)
 *
 * Buy 2/3 → all same colour: same native form approach (qty = 2 or 3)
 *            mixed colours:   Cart AJAX /cart/add.js with items array,
 *                             then section-rendering to update the UI
 */

class UsafeBundleUpsell {

  /* ── Colour map for swatch backgrounds ── */
  static SWATCH_COLORS = {
    purple: '#7B5EA7', violet: '#7B5EA7',
    gray:   '#9B9B9B', grey: '#9B9B9B', silver: '#C0C0C0',
    pink:   '#E8789F', rose: '#E8789F',
    blue:   '#4A8FC2', navy: '#1F3A6E',
    red:    '#D9534F', burgundy: '#800020',
    black:  '#1A1A1A',
    white:  '#F5F5F5',
    green:  '#5A9E6F', olive: '#6B7C3C',
    yellow: '#F5C842', gold: '#D4AF37',
    orange: '#E88B3A',
    cream:  '#F5E6C8', creme: '#F5E6C8', beige: '#F5E6C8',
    brown:  '#8B5E3C',
  };

  constructor(el) {
    this.el = el;
    this.sectionId    = el.dataset.sectionId;
    this.basePrice    = parseInt(el.dataset.basePrice, 10) || 0;
    this.bundle2Pct   = parseInt(el.dataset.bundle2Pct, 10) || 10;
    this.bundle3Pct   = parseInt(el.dataset.bundle3Pct, 10) || 15;
    this.moneyFormat  = decodeURIComponent(el.dataset.moneyFormat || '{{amount}}');
    this.selectedVariantId = parseInt(el.dataset.variantId, 10);
    this.selectedQty  = 1;
    this.slotSelections = {};   // { slotIndex: variantId }

    // Parse variant JSON embedded by Liquid
    const scriptEl = document.getElementById(`BundleVariants-${this.sectionId}`);
    this.variants = scriptEl ? JSON.parse(scriptEl.textContent) : [];

    // DOM refs
    this.radios     = el.querySelectorAll('.bundle-option__radio');
    this.ctaBtn     = el.querySelector(`#BundleAddToCart-${this.sectionId}`);
    this.ctaLabel   = el.querySelector('.bundle-upsell__cta-label');
    this.pickersEl  = document.getElementById(`BundlePickers-${this.sectionId}`);
    this.slotsEl    = document.getElementById(`BundleSlots-${this.sectionId}`);

    this._init();
  }

  /* ─────────────────────────────────── init ── */

  _init() {
    this.radios.forEach(radio => {
      radio.addEventListener('change', () => {
        this.selectedQty = parseInt(radio.value, 10);
        this._onQtyChange();
      });
    });

    this.ctaBtn?.addEventListener('click', () => this._addToCart());

    // Dawn pub/sub: update prices + slot 1 when variant changes
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._unsub = subscribe(PUB_SUB_EVENTS.variantChange, event => {
        if (event?.data?.sectionId !== this.sectionId) return;
        const v = event?.data?.variant;
        if (!v) return;
        this.basePrice = v.price;
        this.selectedVariantId = v.id;
        this.el.dataset.basePrice = v.price;
        this.el.dataset.variantId = v.id;
        this._refreshPrices();
        if (this.selectedQty > 1) this._selectSlot(1, v.id);
      });
    }

    this._updateCTALabel();
  }

  /* ─────────────────────────── qty change ── */

  _onQtyChange() {
    this._updateCTALabel();
    this._refreshPrices();

    if (this.selectedQty > 1) {
      this._buildPickers();
      this.pickersEl?.removeAttribute('hidden');
    } else {
      this.pickersEl?.setAttribute('hidden', '');
    }
  }

  /* ──────────────────────── colour pickers ── */

  _buildPickers() {
    if (!this.slotsEl) return;
    this.slotsEl.innerHTML = '';

    for (let i = 1; i <= this.selectedQty; i++) {
      const row = document.createElement('div');
      row.className = 'bundle-picker-slot';
      row.innerHTML = `
        <span class="bundle-picker-slot__label">Journal ${i}:</span>
        <div class="bundle-picker-slot__swatches" data-slot="${i}"></div>
      `;

      const swatchWrap = row.querySelector('.bundle-picker-slot__swatches');

      this.variants.forEach(v => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bundle-swatch' + (v.available ? '' : ' bundle-swatch--soldout');
        btn.dataset.variantId = v.id;
        btn.title = v.color;
        btn.setAttribute('aria-label', v.color);
        btn.setAttribute('aria-pressed', 'false');
        btn.style.setProperty('--swatch-bg', this._swatchColor(v.color));
        if (!v.available) btn.disabled = true;

        btn.addEventListener('click', () => this._selectSlot(i, v.id));
        swatchWrap.appendChild(btn);
      });

      this.slotsEl.appendChild(row);

      // Default: slot 1 = current product variant, others = first available
      const defaultId = (i === 1)
        ? this.selectedVariantId
        : (this.variants.find(v => v.available)?.id ?? this.selectedVariantId);
      this._selectSlot(i, defaultId);
    }
  }

  _selectSlot(slotIndex, variantId) {
    const wrap = this.slotsEl?.querySelector(`[data-slot="${slotIndex}"]`);
    if (!wrap) return;
    wrap.querySelectorAll('.bundle-swatch').forEach(btn => {
      const match = parseInt(btn.dataset.variantId, 10) === variantId;
      btn.setAttribute('aria-pressed', match ? 'true' : 'false');
    });
    this.slotSelections[slotIndex] = variantId;
  }

  _swatchColor(colorName) {
    return UsafeBundleUpsell.SWATCH_COLORS[colorName?.toLowerCase()] || '#CCCCCC';
  }

  /* ─────────────────── gather cart items ── */

  _getItems() {
    const qty = this.selectedQty;
    const map = {};

    for (let i = 1; i <= qty; i++) {
      const id = this.slotSelections[i] ?? this.selectedVariantId;
      map[id] = (map[id] || 0) + 1;
    }

    return Object.entries(map).map(([id, quantity]) => ({
      id: parseInt(id, 10),
      quantity,
    }));
  }

  /* ───────────────────────── add to cart ── */

  async _addToCart() {
    const qty   = this.selectedQty;
    const items = this._getItems();

    // Buy 1 OR all-same-colour bundle → Dawn native form (handles cart UI)
    if (qty === 1 || items.length === 1) {
      this._nativeFormSubmit(items[0].quantity);
      return;
    }

    // Mixed colours → Cart AJAX
    this.ctaBtn.setAttribute('aria-busy', 'true');
    try {
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items,
          sections: ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'],
        }),
      });

      if (!resp.ok) throw new Error(`Cart error ${resp.status}`);
      const body = await resp.json();
      this._updateCartUI(body);
    } catch (err) {
      console.error('[UsafeBundleUpsell] Cart add failed:', err);
    } finally {
      this.ctaBtn.removeAttribute('aria-busy');
    }
  }

  /* Uses Dawn's existing product form → keeps cart drawer / notification intact */
  _nativeFormSubmit(qty) {
    const form     = document.getElementById(`product-form-${this.sectionId}`);
    const qtyInput = document.getElementById(`Quantity-${this.sectionId}`);
    const submit   = form?.querySelector('[type="submit"][name="add"]');
    if (!submit) return;

    const original = qtyInput?.value ?? '1';
    if (qtyInput) {
      qtyInput.value = qty;
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    submit.click();
    setTimeout(() => {
      if (qtyInput) {
        qtyInput.value = original;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, 600);
  }

  /* Refresh cart UI after a fetch-based add */
  _updateCartUI(body) {
    const sections = body.sections || {};

    // 1. Update cart icon bubble
    if (sections['cart-icon-bubble']) {
      const parser  = new DOMParser();
      const doc     = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      const existing = document.getElementById('cart-icon-bubble');
      const updated  = doc.getElementById('cart-icon-bubble');
      if (existing && updated) existing.replaceWith(updated);
    }

    // 2a. Dawn CartNotification — uses renderContents if available
    const notification = document.querySelector('cart-notification');
    if (notification?.renderContents) {
      const addedItem = Array.isArray(body.items) ? body.items[0] : body;
      notification.renderContents({ key: addedItem?.key ?? '', sections });
      return;
    }

    // 2b. Dawn CartDrawer — publish cartUpdate then open
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        fetch('/cart.js')
          .then(r => r.json())
          .then(cart => publish(PUB_SUB_EVENTS.cartUpdate, { cart, source: 'product-form' }));
      }
      drawer.open?.();
    }
  }

  /* ─────────────────── CTA label update ── */

  _updateCTALabel() {
    if (!this.ctaLabel) return;
    const qty = this.selectedQty;
    if (qty === 1) {
      this.ctaLabel.textContent = 'Add 1 Journal to Cart';
    } else {
      const pct = qty === 2 ? this.bundle2Pct : this.bundle3Pct;
      this.ctaLabel.textContent = `Add ${qty} Journals to Cart — Save ${pct}%`;
    }
  }

  /* ─────────────── price display update ── */

  _refreshPrices() {
    const b  = this.basePrice;
    const p2 = this.bundle2Pct;
    const p3 = this.bundle3Pct;

    const orig2  = b * 2;
    const total2 = orig2  - Math.round(orig2  * p2 / 100);
    const per2   = Math.round(total2 / 2);

    const orig3  = b * 3;
    const total3 = orig3  - Math.round(orig3  * p3 / 100);
    const per3   = Math.round(total3 / 3);

    const set = (sel, val) => {
      const node = this.el.querySelector(sel);
      if (node) node.textContent = val;
    };

    set('.js-bp-per-1',   `${this._money(b)} each`);
    set('.js-bp-total-1', this._money(b));
    set('.js-bp-per-2',   `${this._money(per2)} each`);
    set('.js-bp-orig-2',  this._money(orig2));
    set('.js-bp-total-2', this._money(total2));
    set('.js-bp-per-3',   `${this._money(per3)} each`);
    set('.js-bp-orig-3',  this._money(orig3));
    set('.js-bp-total-3', this._money(total3));

    this._updateCTALabel();
  }

  _money(cents) {
    const fmt    = this.moneyFormat;
    const amount = cents / 100;
    const fixed2 = amount.toFixed(2);
    const noFrac = Math.round(amount).toString();
    return fmt
      .replace('{{amount}}', amount % 1 === 0 ? noFrac : fixed2)
      .replace('{{amount_no_decimals}}', noFrac)
      .replace('{{amount_with_comma_separator}}', fixed2.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}',
               noFrac.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
  }

  destroy() {
    if (this._unsub) this._unsub();
  }
}

document.querySelectorAll('.usafe-bundle-upsell').forEach(el => new UsafeBundleUpsell(el));
